import React, { useState, useCallback, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { getHeader, toastError } from "../../utils/util";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import PageTitle from "../../widgets/PageTitle";
import BackBtn from "../../widgets/BackBtn";
import { API_ENDPOINT, SCHEDULED_EVENTS } from "../../utils/constants";

const ContactGroupForm = () => {
  /* ---------------- state ---------------- */
  const [groupName, setGroupName] = useState("");
  const [columns, setColumns] = useState([{ id: uuidv4(), name: "" }]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dataEntry, setDataEntry] = useState({ phoneNumber: "" });

  /* ---------------- handlers ---------------- */
  const handleAddColumn = useCallback(() => {
    setColumns((prev) => [...prev, { id: uuidv4(), name: "" }]);
  }, []);

  const handleRemoveColumn = useCallback((id) => {
    setColumns((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleColumnChange = useCallback((id, value) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, name: value } : c))
    );
  }, []);

  const handleDataChange = useCallback((field, value) => {
    setDataEntry((prev) => ({ ...prev, [field]: value }));
  }, []);

  const trimmedColumnNames = useMemo(
    () => columns.map((c) => c.name.trim()),
    [columns]
  );

  useEffect(() => {
    if (selectedEvent) {
      if (selectedEvent === "Birthday" || selectedEvent === "Anniversary") {
        const sA = [
          { id: uuidv4(), name: "Name" },
          { id: uuidv4(), name: "DOB" },
          { id: uuidv4(), name: "Anniversary" },
        ];
        setColumns(sA);
      }
    }
  }, [selectedEvent]);

  const validateForm = useCallback(() => {
    if (!groupName.trim()) {
      toast.error("Please enter a group name.");
      return false;
    }

    if (trimmedColumnNames.some((name) => !name)) {
      toast.error("Column names can't be empty.");
      return false;
    }

    if (new Set(trimmedColumnNames).size !== trimmedColumnNames.length) {
      toast.error("Column names must be unique.");
      return false;
    }

    return true;
  }, [groupName, trimmedColumnNames]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      const payload = {
        groupName: groupName.trim(),
        schema: ["phoneNumber", ...trimmedColumnNames],
      };
      setOpenDialog(true);
      return;

      try {
        setLoading(true);
        await axios.post("/api/contact-groups", payload);

        // Initialize data entry
        const initialData = { phoneNumber: "" };
        trimmedColumnNames.forEach((name) => {
          initialData[name] = "";
        });
        setDataEntry(initialData);
        setOpenDialog(true);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [groupName, trimmedColumnNames, validateForm]
  );

  const validateDataEntry = useCallback(() => {
    if (!dataEntry.phoneNumber.trim()) {
      toast.error("Phone number is required.");
      return false;
    }

    if (trimmedColumnNames.some((name) => !dataEntry[name]?.trim())) {
      toast.error("All fields must be filled.");
      return false;
    }

    return true;
  }, [dataEntry, trimmedColumnNames]);

  const handleDialogSubmit = useCallback(async () => {
    if (!validateDataEntry()) return;
    try {
      setLoading(true);
      await axios.post(
        `${API_ENDPOINT}/user/create-contact-group`,
        {
          groupName: groupName.trim(),
          ...dataEntry,
        },
        getHeader()
      );
      toast.success("Data entry added successfully!");
      setOpenDialog(false);
      resetForm();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dataEntry, groupName, validateDataEntry]);

  const resetForm = useCallback(() => {
    setGroupName("");
    setSelectedEvent("");
    setColumns([{ id: uuidv4(), name: "" }]);
    setDataEntry({ phoneNumber: "" });
  }, []);

  const handleDialogClose = useCallback(
    (event, reason) => {
      if (reason === "backdropClick") {
        // ignore outside clicks
        return;
      }

      setOpenDialog(false);
    },
    [resetForm]
  );

  /* ---------------- UI ---------------- */
  return (
    <Container sx={{ pt: 2 }} maxWidth="md">
      <BackBtn />
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, md: 4 }, maxWidth: 600, mx: "auto", mt: 1 }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Stack spacing={3}>
          <PageTitle>Create Contact Group</PageTitle>
          <TextField
            label="Contact-Group Name *"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            disabled={!!selectedEvent}
            size="small"
          />
          <Box my={1} display={"flex"} gap={2}>
            Select Group:{" "}
            <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
              {SCHEDULED_EVENTS.map((item) => (
                <Chip
                  color={selectedEvent === item ? "success" : "info"}
                  variant={selectedEvent === item ? "filled" : "outlined"}
                  clickable
                  onClick={() => {
                    if (item === selectedEvent) {
                      setSelectedEvent("");
                      setColumns([{ id: uuidv4(), name: "" }]);
                    } else {
                      setSelectedEvent(item);
                      setGroupName(item);
                    }
                  }}
                  label={item}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography fontWeight={500} mb={1}>
              Columns
            </Typography>
            <Chip
              label="phoneNumber (mandatory)"
              color="primary"
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <Stack spacing={2}>
              {columns.map(({ id, name }, idx) => (
                <Stack key={id} direction="row" spacing={1} alignItems="center">
                  <TextField
                    label={`Column ${idx + 1}`}
                    value={name}
                    disabled={!!selectedEvent}
                    onChange={(e) => handleColumnChange(id, e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleRemoveColumn(id)}
                    disabled={columns.length === 1 || !!selectedEvent}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                startIcon={<Add />}
                disabled={!!selectedEvent}
                onClick={handleAddColumn}
                sx={{ alignSelf: "flex-start" }}
              >
                Add Column
              </Button>
            </Stack>
          </Box>

          <Box display={"flex"} justifyContent={"end"}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ alignSelf: "flex-start" }}
            >
              {loading ? "Saving…" : "Create Group"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Data Entry Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter one Contact Data</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Phone Number *"
              value={dataEntry.phoneNumber}
              onChange={(e) => handleDataChange("phoneNumber", e.target.value)}
              fullWidth
              size="small"
            />
            {trimmedColumnNames.map((name) => (
              <TextField
                key={name}
                label={name}
                placeholder={name}
                InputLabelProps={{
                  shrink: true,
                }}
                type={
                  name === "DOB" || name === "Anniversary" ? "date" : "text"
                }
                value={dataEntry[name] || ""}
                onChange={(e) => handleDataChange(name, e.target.value)}
                fullWidth
                size="small"
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={loading}
          >
            {loading ? "Submitting…" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContactGroupForm;
